import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { NextPage } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import HtmlToReact from 'html-to-react';

import { Routes } from 'types/routes';

import { Roles } from 'models/Article';

import { imageResizer } from 'utils/image';
import { selectCmsPageSection } from 'utils/locale';
import { withVariables } from 'utils/string';

import LocaleContext from 'components/context/Locale';
import Head from 'components/layout/Head';
import LayoutContainer from 'components/layout/LayoutContainer';
import PageContainer from 'components/layout/PageContainer';
import ArticleIndex from 'components/modules/public/layout/articles/ArticleIndex/component';
import ArticleWrapper from 'components/modules/public/layout/articles/ArticleWrapper/component';
import Author from 'components/modules/public/layout/articles/Author';
import Breadcrumbs from 'components/modules/public/layout/articles/Breadcrumbs';
import NewestJobs from 'components/modules/public/layout/articles/NewestJobs';
import OffersInArticles from 'components/modules/public/layout/articles/OffersInArticles';
import ShareButton from 'components/modules/public/layout/controls/ShareButton';
import LayoutDefault from 'components/modules/public/layouts/Default';
import Page404 from 'components/modules/public/pages/404';

import SectionNewsletter from './sections/Newsletter';
import StyledComponent from './styles';
import { Props } from './types';

const PublicPageArticleEmployee: NextPage<Props> = ({ article, jobs, cmsPage, notFound }) => {
    const [scrollPosition, setScrollPotion]:[number, Function] = useState(0);
    const [visibleSecondArticle, setVisibleSecondArticle]: [boolean, Function] = useState(false);

    const refFirstArticle = useRef(null);
    const router = useRouter();
    const { publicRuntimeConfig } = getConfig();
    const localeContext = useContext(LocaleContext);
    const data = localeContext?.translations?.articleEmployee;

    if (!article || notFound) {
        return (<Page404 />);
    }

    const htmlInput = article?.content
        .replace('&lt;newestjobs /&gt;', '<newestjobs />');
    const HtmlToReactParser = HtmlToReact.Parser;
    const htmlToReactParser = new HtmlToReactParser();
    const isValidNode = function () {
        return true;
    };

    const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);
    const processingInstructions = [{
        replaceChildren: false,
        shouldProcessNode (node) {
            return node.name === 'newestjobs';
        },
        processNode () {
            return (
                <NewestJobs
                    jobs={jobs}
                    jobsCopy={data.content.jobsCopy}
                />
            );
        },
    },
    {
        shouldProcessNode () {
            return true;
        },
        processNode: processNodeDefinitions.processDefaultNode,
    }];

    const reactComponentContent = htmlToReactParser.parseWithInstructions(htmlInput, isValidNode, processingInstructions);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const reactHtmlContent = ReactDOMServer.renderToStaticMarkup(reactComponentContent);
    const secondArticle = article?.similarArticles[0] ? { ...article?.similarArticles[0] } : null;


    let breadcrumbs = [{
        name: article.title,
        path: article.href,
    }];
    let breadcrumbsSecondArticle = null;

    if (secondArticle) breadcrumbsSecondArticle = [{
        name: secondArticle.title,
        path: secondArticle.href,
    }];


    if (article.role === Roles.Worker) {
        breadcrumbs = [
            {
                name: data?.content?.breadcrumbs?.home,
                path: Routes.PublicHome,
            },
            {
                name: data?.content?.breadcrumbs?.blog,
                path: Routes.PublicBlog,
            },
            {
                name: article?.category?.name,
                path: article?.category?.href,
            },
            ...breadcrumbs,
        ];

        if (secondArticle) breadcrumbsSecondArticle = [
            {
                name: data?.content?.breadcrumbs?.home,
                path: Routes.PublicHome,
            },
            {
                name: data?.content?.breadcrumbs?.blog,
                path: Routes.PublicBlog,
            },
            {
                name: article?.category?.name,
                path: article?.category?.href,
            },
            ...breadcrumbsSecondArticle,
        ];
    }

    useEffect(() => {
        if(secondArticle) {
            const firstArticleSlug =  withVariables(Routes.PublicBlogPost, {
                articleCategorySlug: article?.category?.slug,
                slug: article?.slug,
            });
            const secondArticleSlug = withVariables(Routes.PublicBlogPost, {
                articleCategorySlug: article?.category?.slug,
                slug: secondArticle?.slug,
            });
            const userWindowHeight = window.innerHeight;
            const firstArticleHight = refFirstArticle.current?.clientHeight;
            let thresholdToUse = (userWindowHeight / firstArticleHight) - 0.1;
            if(thresholdToUse <= 0) {
                thresholdToUse = 0.01;
            }
            else if(thresholdToUse > .015 && thresholdToUse < 0.2) {
                thresholdToUse = 0.1;
            }
            else if(thresholdToUse >= .02) {
                thresholdToUse = 0.2;
            }
            const callbackFunction = (entries, observer) => {
                const [entry] = entries;
                let path;
                if(window !== undefined) {
                    path = window.location.pathname;
                }
                if(!entry.isIntersecting ) {
                    if(path === firstArticleSlug) {
                        router.replace(withVariables(Routes.PublicBlogPost, {
                            articleCategorySlug: article?.category?.slug,
                            slug: secondArticle?.slug,
                        }), undefined, { shallow: true });
                    }
                    if(!visibleSecondArticle) setVisibleSecondArticle(true);
                } else if (entry.isIntersecting) {
                    if( path === secondArticleSlug) {
                        router.replace(withVariables(Routes.PublicBlogPost, {
                            articleCategorySlug: article?.category?.slug,
                            slug: article?.slug,
                        }), undefined, { shallow: true });
                    }
                }
            };
            const options = {
                root: null,
                rootMargin: '0px',
                threshold:  thresholdToUse,
            };
            const observer = new IntersectionObserver(callbackFunction, options);
            if(refFirstArticle.current) observer.observe(refFirstArticle.current);
        } else {
            return;
        }
    }, []);

    return (
        <StyledComponent className="public-page-article-employee">
            <Head
                {...data.meta}
                title={article.metaTitle || article.title || cmsPage?.metaTitle}
                description={article.metaDescription || article.lead || cmsPage?.metaDescription}
                canonical={data.meta.canonical?.replace(':articleCategorySlug', article?.category?.slug).replace(':slug', article.slug)}
                image={article?.ogPhoto?.imageUrl || cmsPage?.image?.imageUrl || data?.meta?.image}
            />
            <LayoutDefault>
                <PageContainer>
                    {/* First article */}
                    <div
                        ref={refFirstArticle}
                        className="first"
                    >
                        <ArticleWrapper article={article}>
                            {(content, indexStructure) => {
                                const articleSplited = content.split(/<p>|<\/p>/);
                                const firstTwoParagraphs = `${articleSplited[0]}</p>${articleSplited[1]}</p>`;
                                articleSplited.splice(0, 2);
                                const otherParagraphs = articleSplited.reduce((acc: string, curr: string) => `${acc}${curr}</p>`);

                                return (
                                    <>
                                        <LayoutContainer>
                                            <Breadcrumbs
                                                routes={[
                                                    ...breadcrumbs,
                                                ]}
                                                isSponsored={article?.isSponsored || false}
                                                sponsoredCopy={selectCmsPageSection(cmsPage, 'content')?.sponsoredArticle || data?.content?.sponsoredArticle}
                                            />
                                        </LayoutContainer>
                                        <LayoutContainer className='article-main'>
                                            <div className="article-content">
                                                <div className="header">
                                                    <h1 className="headline">
                                                        {article?.title}
                                                    </h1>
                                                    <ShareButton
                                                        route={Routes.PublicBlogPost}
                                                        variables={{
                                                            articleCategorySlug: article?.category?.slug,
                                                            slug: article.slug,
                                                        }}
                                                    />
                                                </div>

                                                <p
                                                    className="lead"
                                                    dangerouslySetInnerHTML={{ __html: article?.lead }}
                                                />

                                                {article?.coverImage && (
                                                    <img
                                                        className="article-image"
                                                        src={imageResizer(article?.coverImage.imageUrl, { width: 1920 })}
                                                        alt={article.imageAltText}
                                                        loading="lazy"
                                                        width="1366"
                                                        height="450"
                                                    />
                                                )}

                                                <ArticleIndex indexStructure={indexStructure} />

                                                <div
                                                    className="article-txt"
                                                    dangerouslySetInnerHTML={{ __html: firstTwoParagraphs }}
                                                />

                                               {/*  { article?.jobs && (
                                                    <OffersInArticles
                                                        title={selectCmsPageSection(cmsPage, 'content')?.offersTitle || data?.content?.offersTitle}
                                                        jobs={article?.jobs}
                                                    />
                                                )} */}

                                                <div
                                                    className="article-txt"
                                                    dangerouslySetInnerHTML={{ __html: otherParagraphs }}
                                                />

                                                {article?.isSponsored && article?.sponsoredCompanyName && (
                                                    <p
                                                        className="article-sponsor"
                                                        dangerouslySetInnerHTML={{ __html: selectCmsPageSection(cmsPage, 'content')?.sponsoredCompanyName.replace('{companyName}', article?.sponsoredCompanyName) || data?.content?.sponsoredCompanyName?.replace('{companyName}', article?.sponsoredCompanyName),
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            {article.articleAuthor && (
                                                <Author
                                                    authorImage={article?.articleAuthor?.image?.imageUrl}
                                                    authorName={article?.articleAuthor?.fullName}
                                                    authorDescription={article?.articleAuthor?.description}
                                                />
                                            )}
                                        </LayoutContainer>
                                    </>
                                );
                            }}
                        </ArticleWrapper>
                        <div ></div>
                    </div>

                    {/* Second article */}
                    { secondArticle && visibleSecondArticle && (
                        <ArticleWrapper
                            article={secondArticle}
                            customClassName="second-article-wrapper"
                        >
                            {(content, indexStructure) => {
                                const articleSplited = content.split(/<p>|<\/p>/);
                                const firstTwoParagraphs = `${articleSplited[0]}</p>${articleSplited[1]}</p>`;
                                articleSplited.splice(0, 2);
                                const otherParagraphs = articleSplited.reduce((acc: string, curr: string) => `${acc}${curr}</p>`);

                                return (
                                    <>
                                        <LayoutContainer>
                                            <Breadcrumbs
                                                routes={[
                                                    ...breadcrumbsSecondArticle,
                                                ]}
                                                isSponsored={secondArticle?.isSponsored || false}
                                                sponsoredCopy={selectCmsPageSection(cmsPage, 'content')?.sponsoredArticle || data?.content?.sponsoredArticle}
                                            />
                                        </LayoutContainer>
                                        <LayoutContainer className='article-main article-second'>
                                            <div className="article-content">
                                                <div className="header">
                                                    <h1 className="headline">
                                                        {secondArticle?.title}
                                                    </h1>
                                                    <ShareButton
                                                        route={Routes.PublicBlogPost}
                                                        variables={{
                                                            articleCategorySlug: secondArticle?.category?.slug,
                                                            slug: secondArticle.slug,
                                                        }}
                                                    />
                                                </div>

                                                <p
                                                    className="lead"
                                                    dangerouslySetInnerHTML={{ __html: secondArticle?.lead }}
                                                />

                                                {secondArticle?.coverImage && (
                                                    <img
                                                        className="article-image"
                                                        src={imageResizer(secondArticle?.coverImage.imageUrl, { width: 1920 })}
                                                        alt={article.imageAltText}
                                                        loading="lazy"
                                                        width="1366"
                                                        height="450"
                                                    />
                                                )}

                                                <ArticleIndex
                                                    indexStructure={indexStructure}
                                                    style="white"
                                                />

                                                <div
                                                    className="article-txt"
                                                    dangerouslySetInnerHTML={{ __html: firstTwoParagraphs }}
                                                />

                                                {/* { article?.jobs && (
                                                    <OffersInArticles
                                                        title={selectCmsPageSection(cmsPage, 'content')?.offersTitle || data?.content?.offersTitle}
                                                        jobs={article?.jobs}
                                                    />
                                                )} */}

                                                <div
                                                    className="article-txt"
                                                    dangerouslySetInnerHTML={{ __html: otherParagraphs }}
                                                />

                                                { secondArticle?.isSponsored && secondArticle?.sponsoredCompanyName && (
                                                    <p
                                                        className="article-sponsor"
                                                        dangerouslySetInnerHTML={{ __html: selectCmsPageSection(cmsPage, 'content')?.sponsoredCompanyName.replace('{companyName}', secondArticle?.sponsoredCompanyName) || data?.content?.sponsoredCompanyName?.replace('{companyName}', secondArticle?.sponsoredCompanyName) }}
                                                    />
                                                )}
                                            </div>
                                            {secondArticle.articleAuthor && (
                                                <Author
                                                    authorImage={secondArticle?.articleAuthor?.image?.imageUrl}
                                                    authorName={secondArticle?.articleAuthor?.fullName}
                                                    authorDescription={secondArticle?.articleAuthor?.description}
                                                />
                                            )}
                                        </LayoutContainer>
                                    </>
                                );
                            }}
                        </ArticleWrapper>
                    )}
                    {/* <SectionTutorials
                        {...data.content.tutorials}
                        articles={tutorials}
                    /> */}
                    <SectionNewsletter
                        translations={{
                            ...data.content.newsletter,
                            ...selectCmsPageSection(cmsPage, 'content')?.newsletter,
                        }}
                    />
                </PageContainer>
            </LayoutDefault>
        </StyledComponent>
    );
};

export default PublicPageArticleEmployee;